import React, { useState } from "react";
import { Phone, Mail, MapPin, Clock, Send, MessageCircle } from "lucide-react";
import { siteData } from "../mock";

export default function Contact() {
  const { brand } = siteData;
  const [form, setForm] = useState({ name: "", phone: "", req: "" });
  const [sent, setSent] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) return;
    // store locally so the "submission" feels real
    try {
      const all = JSON.parse(localStorage.getItem("jk_enquiries") || "[]");
      all.push({ ...form, ts: new Date().toISOString() });
      localStorage.setItem("jk_enquiries", JSON.stringify(all));
    } catch (err) { /* ignore */ }
    setSent(true);
    setTimeout(() => { setSent(false); setForm({ name: "", phone: "", req: "" }); }, 3500);
  };

  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12 md:gap-16">
        <div className="lg:col-span-6">
          <div className="text-[11px] tracking-[0.32em] text-[#b88b4a] mb-5">GET IN TOUCH</div>
          <h2 className="font-serif text-neutral-900 text-[42px] md:text-[64px] leading-[1.04] tracking-tight">
            Let&apos;s find your <span className="italic">next address.</span>
          </h2>
          <p className="mt-6 text-neutral-700 text-[15.5px] md:text-[17px] leading-[1.8] max-w-lg">
            Call, WhatsApp or drop by our Mohali office. We respond to every enquiry within 2 working hours — {brand.hours}.
          </p>

          <div className="mt-10 space-y-6">
            <div className="flex gap-4">
              <Phone className="w-5 h-5 text-[#b88b4a] mt-1 shrink-0" strokeWidth={1.5} />
              <div>
                <div className="text-[10.5px] tracking-[0.3em] text-neutral-500 mb-1.5">PHONE</div>
                <div className="text-neutral-900 text-[15.5px]">
                  <a href={`tel:${brand.primaryPhoneRaw}`} className="hover:text-[#b88b4a]">{brand.primaryPhone}</a>
                  <span className="text-neutral-400 mx-2">/</span>
                  <a href={`tel:${brand.altPhoneRaw}`} className="hover:text-[#b88b4a]">{brand.altPhone}</a>
                </div>
              </div>
            </div>
            <div className="flex gap-4">
              <MapPin className="w-5 h-5 text-[#b88b4a] mt-1 shrink-0" strokeWidth={1.5} />
              <div>
                <div className="text-[10.5px] tracking-[0.3em] text-neutral-500 mb-1.5">OFFICE</div>
                <div className="text-neutral-900 text-[15.5px]">{brand.address}</div>
                <div className="text-neutral-600 text-[13.5px] mt-1">{brand.coverage}</div>
              </div>
            </div>
            <div className="flex gap-4">
              <Clock className="w-5 h-5 text-[#b88b4a] mt-1 shrink-0" strokeWidth={1.5} />
              <div>
                <div className="text-[10.5px] tracking-[0.3em] text-neutral-500 mb-1.5">HOURS</div>
                <div className="text-neutral-900 text-[15.5px]">{brand.hours}</div>
              </div>
            </div>
            <div className="flex gap-4">
              <Mail className="w-5 h-5 text-[#b88b4a] mt-1 shrink-0" strokeWidth={1.5} />
              <div>
                <div className="text-[10.5px] tracking-[0.3em] text-neutral-500 mb-1.5">EMAIL</div>
                <a href={`mailto:${brand.email}`} className="text-neutral-900 text-[15.5px] hover:text-[#b88b4a]">{brand.email}</a>
              </div>
            </div>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <a href={`tel:${brand.primaryPhoneRaw}`} className="btn-gold inline-flex items-center justify-center gap-2 px-6 py-3.5 text-[13px] tracking-wide font-medium">
              <Phone className="w-4 h-4" /> Call Now
            </a>
            <a href={`https://wa.me/${brand.whatsapp}`} target="_blank" rel="noopener noreferrer" className="btn-whatsapp inline-flex items-center justify-center gap-2 px-6 py-3.5 text-[13px] tracking-wide">
              <MessageCircle className="w-4 h-4" /> WhatsApp Us
            </a>
          </div>
        </div>

        <div className="lg:col-span-6">
          <div className="bg-jk-cream border border-[#e7e2d7] p-7 md:p-10">
            <div className="text-[11px] tracking-[0.3em] text-[#b88b4a] mb-3">SEND AN ENQUIRY</div>
            <h3 className="font-serif text-neutral-900 text-[28px] md:text-[34px] leading-tight">
              Tell us what <span className="italic">you&apos;re looking for.</span>
            </h3>
            <form onSubmit={submit} className="mt-7 space-y-5">
              <div>
                <label className="text-[10.5px] tracking-[0.25em] text-neutral-600 uppercase">Name</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your full name" className="mt-2 w-full bg-white border border-neutral-300 focus:border-neutral-900 focus:outline-none px-4 py-3 text-[14.5px] text-neutral-900" />
              </div>
              <div>
                <label className="text-[10.5px] tracking-[0.25em] text-neutral-600 uppercase">Phone</label>
                <input required type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 9XXXXXXXXX" className="mt-2 w-full bg-white border border-neutral-300 focus:border-neutral-900 focus:outline-none px-4 py-3 text-[14.5px] text-neutral-900" />
              </div>
              <div>
                <label className="text-[10.5px] tracking-[0.25em] text-neutral-600 uppercase">Your requirement</label>
                <textarea rows={4} value={form.req} onChange={(e) => setForm({ ...form, req: e.target.value })} placeholder="Tell us about the property you're looking for — area, budget, purpose..." className="mt-2 w-full bg-white border border-neutral-300 focus:border-neutral-900 focus:outline-none px-4 py-3 text-[14.5px] text-neutral-900" />
              </div>
              <button type="submit" disabled={sent} className={`btn-dark w-full inline-flex items-center justify-center gap-2 py-4 text-[13.5px] tracking-wide ${sent ? "opacity-70" : ""}`}>
                {sent ? "Thank you — we'll call you back shortly." : (<><Send className="w-4 h-4" /> Send Message</>)}
              </button>
              <p className="text-[12px] text-neutral-500">By submitting, you agree to be contacted by JK Estates on the phone number provided.</p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
